// ─────────────────────────────────────────────────────────────────────────────
// Offering Strength — deterministic 0–100 completeness rating for offering
// cards, following the persona-strength pattern (shared/persona-strength.ts).
//
// The score grades DEPTH across the structured offering model, not just
// presence: developed copy fields earn full credit while stubs earn half,
// and list fields earn linear credit up to a per-field item target. The same
// computation drives the card gauge, the "Offer Ready" badge, and the grid's
// default Strength sort, so they can never disagree.
//
// Hero imagery doesn't live on a single offer column (the card resolves a
// fallback chain: offer reference images → first included product's photo) —
// the card passes the resolved `hasHeroPhoto` flag in. When absent that
// dimension simply scores 0.
// ─────────────────────────────────────────────────────────────────────────────

export interface OfferStrengthInput {
  name?: string | null;
  offerType?: string | null;
  offerCategory?: string | null;
  summary?: string | null;
  shortDescription?: string | null;
  buyerProblem?: unknown;
  desiredOutcome?: unknown;
  valueProposition?: unknown;
  offerDetails?: unknown;
  fulfillment?: unknown;
  proof?: unknown;
  riskReversal?: unknown;
  objections?: unknown;
  conversion?: unknown;
  personaFit?: unknown;
  creativeInputs?: unknown;
  targetFunnelStage?: unknown;
  targetPersonas?: unknown;
  sketchImageUrl?: string | null;
  // Resolved by the card from the hero-photo fallback chain (offer reference
  // images → first included product's photo). Optional everywhere else.
  hasHeroPhoto?: boolean | null;
}

/** Score at or above which an offering is considered "ready" (badge + copy). */
export const OFFER_STRENGTH_READY_THRESHOLD = 70;

export function isOfferStrengthReady(score: number): boolean {
  return score >= OFFER_STRENGTH_READY_THRESHOLD;
}

/** One-line readiness caption shown under the detail page's strength ring. */
export function offerStrengthCaption(score: number): string {
  if (score >= 90) return "Exceptionally complete. Ready to activate.";
  if (score >= OFFER_STRENGTH_READY_THRESHOLD) return "Strong positioning. Ready to activate.";
  if (score >= 40) return "Solid foundation. Fill the gaps to strengthen it.";
  return "Early draft. Add detail to build strength.";
}

/** True when the entry carries real content (non-blank string, or an object
 *  with at least one non-blank string value). */
function isMeaningfulEntry(entry: unknown): boolean {
  if (entry == null) return false;
  if (typeof entry === "string") return entry.trim().length > 0;
  if (typeof entry === "number" || typeof entry === "boolean") return true;
  if (Array.isArray(entry)) return entry.some(isMeaningfulEntry);
  if (typeof entry === "object") {
    return Object.values(entry as Record<string, unknown>).some(isMeaningfulEntry);
  }
  return false;
}

/** Count of meaningful items in a jsonb list field. Non-array truthy values
 *  (legacy single-string/object rows) count as one item. */
export function meaningfulItemCount(value: unknown): number {
  if (value == null) return 0;
  if (Array.isArray(value)) return value.filter(isMeaningfulEntry).length;
  return isMeaningfulEntry(value) ? 1 : 0;
}

/** Graded credit for a free-text field: 0 empty, half for a stub (<40 chars),
 *  full for developed text. */
function textScore(value: unknown, weight: number): number {
  const len = typeof value === "string" ? value.trim().length : 0;
  if (len === 0) return 0;
  return len >= 40 ? weight : weight / 2;
}

/** Depth-graded credit for a list field: linear up to `target` items. */
function listScore(value: unknown, target: number, weight: number): number {
  const n = meaningfulItemCount(value);
  return Math.min(n / target, 1) * weight;
}

function obj(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Deterministic 0–100 offering strength.
 *
 * Weight budget (sums to 100):
 *  - Core copy (24): identity (name+type) 3, shortDescription 5, summary 4,
 *    buyerProblem.primary_problem 4, desiredOutcome.primary_outcome 4,
 *    valueProposition.one_liner 4 — text fields graded by developed length.
 *  - Structured depth (36): core_benefits 5/3·items, differentiators 4/3,
 *    what_is_included 5/3, process_steps 3/3, proof (cases+testimonials+
 *    metrics+trust signals) 6/3, objections 5/2, personaFit 3/1,
 *    conversion.cta_copy 2, riskReversal.guarantee 3.
 *  - Creative inputs (18): hooks 5/2, angles 4/2, claims 3/2, tone 2,
 *    visual_direction 2/2, landing_page_messages 2/2.
 *  - Targeting (12): targetFunnelStage 3, targetPersonas 3, offerCategory 2,
 *    conversion.conversion_url 2, desiredOutcome.emotional_outcome 2.
 *  - Imagery (10): hero photo 6, quote-strip sketch 4.
 */
export function computeOfferStrength(o: OfferStrengthInput): number {
  let score = 0;

  const bp = obj(o.buyerProblem);
  const outcome = obj(o.desiredOutcome);
  const vp = obj(o.valueProposition);
  const details = obj(o.offerDetails);
  const fulfillment = obj(o.fulfillment);
  const proof = obj(o.proof);
  const risk = obj(o.riskReversal);
  const conversion = obj(o.conversion);
  const creative = obj(o.creativeInputs);

  // Core copy — 24
  if ((o.name ?? "").trim() && (o.offerType ?? "").trim()) score += 3;
  score += textScore(o.shortDescription, 5);
  score += textScore(o.summary, 4);
  score += textScore(bp.primary_problem, 4);
  score += textScore(outcome.primary_outcome, 4);
  score += textScore(vp.one_liner, 4);

  // Structured depth — 36
  score += listScore(vp.core_benefits, 3, 5);
  score += listScore(vp.differentiators, 3, 4);
  score += listScore(details.what_is_included, 3, 5);
  score += listScore(fulfillment.process_steps, 3, 3);
  const proofItems =
    meaningfulItemCount(proof.case_studies) +
    meaningfulItemCount(proof.testimonials) +
    meaningfulItemCount(proof.metrics) +
    meaningfulItemCount(proof.trust_signals);
  score += Math.min(proofItems / 3, 1) * 6;
  score += listScore(o.objections, 2, 5);
  score += listScore(o.personaFit, 1, 3);
  score += textScore(conversion.cta_copy, 2) === 0 ? 0 : 2; // any CTA copy counts full
  score += textScore(risk.guarantee, 3);

  // Creative inputs — 18
  score += listScore(creative.hooks, 2, 5);
  score += listScore(creative.angles, 2, 4);
  score += listScore(creative.claims, 2, 3);
  if (typeof creative.tone === "string" && creative.tone.trim()) score += 2;
  score += listScore(creative.visual_direction, 2, 2);
  score += listScore(creative.landing_page_messages, 2, 2);

  // Targeting — 12
  if (meaningfulItemCount(o.targetFunnelStage) > 0) score += 3;
  if (meaningfulItemCount(o.targetPersonas) > 0) score += 3;
  if ((o.offerCategory ?? "").trim()) score += 2;
  if (typeof conversion.conversion_url === "string" && conversion.conversion_url.trim()) score += 2;
  score += textScore(outcome.emotional_outcome, 2) === 0 ? 0 : 2;

  // Imagery — 10
  if (o.hasHeroPhoto) score += 6;
  if ((o.sketchImageUrl ?? "").trim()) score += 4;

  return Math.max(0, Math.min(100, Math.round(score)));
}
